/**
 * Feature slugs WITHHELD from every QA/prod build. The list itself is xconfig's (src/data/withheld-features.json, the one
 * declaration since 2026-10-11, emitted as dist/withheld-features.json); `withheld-features.generated.json` here is a
 * byte-guarded copy of that file (`npm run copy:withheld-list`, pinned by
 * utils/__tests__/withheld-features.xconfig.drift.spec.ts). This module is the web's only reader of it.
 *
 * Provenance: owner request 2026-09-27 (same reason as xconfig's feature-metadata-generator.ts
 * withhold of 2026-09-06): /pdf-editor stays unreleased until the owner releases it, while local
 * `next dev` keeps serving it (manual-qa/pdf-editor/tools/dev-route.sh writes the gitignored
 * ssr-data/build/pdf-editor.json). Review item B1 (manual-qa/pdf-editor/review/REVIEW.md,
 * 2026-10-04): the withhold used to rely on that json being ABSENT from whichever checkout a build
 * ran in — qa-deploy.sh never runs `clear:copied`, so a build from a dev checkout shipped the route
 * and its sitemap row. This list makes the withhold hold by construction instead.
 *
 * Readers (keep them on THIS list, never a local copy):
 *   - pages/[feature].tsx getStaticPaths (route emission)
 *   - scripts/generate-sitemap.js via scripts/sitemap-filters.js (sitemap rows)
 *   - utils/next-tool.ts (result-card chips never point at a withheld page)
 * Other readers of the same xconfig list: xconfig's generators (no route rows), xconvert-web-ssr
 * src/routes/withheld-features.generated.json (PDF hub links) and professorx resources/withheld-features.json
 * (feature-search index, review item B2). This file is the build-side belt and braces.
 *
 * Runtime assets of a withheld page are not copied by `npm run copy:all` either (REVIEW-5 I20,
 * 2026-10-05): `copy:all` used to run `copy:tesseract-ocr`, so EVERY web build needed the OCR
 * packages (`scripts/copy-tesseract-ocr.js` exits 1 without them, and `xcli/deploy/qa-deploy.sh`
 * dies on a failed `copy:all`) and every QA/prod artefact carried ~23 MB under
 * `out/pdf-editor/ocr/` for a page nobody can reach. They are a separate step,
 * `npm run copy:withheld`, which copies only under XC_INCLUDE_WITHHELD=1 (the same switch as
 * above). Local dev gets them from `manual-qa/pdf-editor/tools/dev-route.sh`, which runs it, and
 * lane/prod-build worktrees link the shared checkout's `public/pdf-editor/ocr`.
 *
 * To release a slug: delete its row in xconfig src/data/withheld-features.json, rebuild xconfig, run
 * `npm run copy:withheld-list` here (and the ssr / professorx copies), and move its `copy:withheld` step(s) into
 * `copy:all`.
 */
import withheldFeatures from './withheld-features.generated.json';

export const WITHHELD_SLUGS: readonly string[] = Object.freeze(withheldFeatures.features.map(f => f.slug));

/** Narrow env view so callers (and tests) can pass a plain object instead of process.env. */
export interface WithholdEnv {
    NODE_ENV?: string;
    XC_INCLUDE_WITHHELD?: string;
}

/**
 * True when a build may include the withheld slugs: the local dev server (`next dev` sets
 * NODE_ENV=development; `next build` always sets production) or an explicit opt-in
 * (XC_INCLUDE_WITHHELD=1, the same switch xconfig honours). Nothing else lifts the withhold.
 */
export function includeWithheldSlugs(env: WithholdEnv = process.env): boolean {
    return env.NODE_ENV === 'development' || env.XC_INCLUDE_WITHHELD === '1';
}

/** True when `slug` must be left out of the artefact being produced under `env`. */
export function withheldFromBuild(slug: string, env: WithholdEnv = process.env): boolean {
    return WITHHELD_SLUGS.includes(slug) && !includeWithheldSlugs(env);
}
